import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../../context/apiClient";

const pad = (n) => String(n).padStart(2, "0");
const defaultEndDateTimeLocal = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const lastDay = new Date(y, m + 1, 0);
    return `${lastDay.getFullYear()}-${pad(lastDay.getMonth() + 1)}-${pad(
        lastDay.getDate()
    )}T23:59`;
};
const toSpringLocalDateTimeParam = (dtLocal) =>
    !dtLocal ? "" : dtLocal.length === 16 ? `${dtLocal}:00` : dtLocal;
const numberFmt = (n) =>
    typeof n === "number" && Number.isFinite(n) ? n.toFixed(0) : "-";

const ROLE_KEYS = { INSTRUCTOR: "INSTRUCTOR", MECHANIC: "MECHANIC", GUEST_MANAGER: "GUEST_MANAGER" };
const ROLE_LABELS = {
    INSTRUCTOR: "Instruktoren",
    MECHANIC: "Mechaniker",
    GUEST_MANAGER: "Guestmanager",
};
const SORT_FIELDS = [
    { value: "lastName", label: "Nachname" },
    { value: "overtime", label: "Überstunden" },
    { value: "expectedOvertime", label: "Erwartete Überstunden" },
];
const ROLE_HEADER_BG = {
    INSTRUCTOR: "bg-indigo-50 border-indigo-100",
    MECHANIC: "bg-emerald-50 border-emerald-100",
    GUEST_MANAGER: "bg-rose-50 border-rose-100",
};

const Forecast = () => {
    const [endTimeLocal, setEndTimeLocal] = useState(defaultEndDateTimeLocal());
    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const [sortField, setSortField] = useState("lastName");
    const [sortDirection, setSortDirection] = useState("asc"); // "asc" | "desc"


    const fetchAll = async () => {
        setLoading(true);
        setErr("");
        try {
            const endTimeParam = toSpringLocalDateTimeParam(endTimeLocal);
            const [forecastRes, empRes] = await Promise.all([
                apiClient.get("/api/employee/getForecast", {
                    withCredentials: true,
                    params: { endTime: endTimeParam },
                }),
                apiClient.get("/api/employee/all", { withCredentials: true }),
            ]);
            setItems(forecastRes.data || []);
            setEmployees(empRes.data || []);
        } catch (e) {
            console.error(e);
            setErr("Fehler beim Abrufen des Forecasts.");
            setItems([]);
            setEmployees([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const roleByName = useMemo(() => {
        const map = new Map();
        for (const e of employees) {
            const k = `${(e.firstName || "").trim().toLowerCase()}|${(e.lastName || "")
                .trim()
                .toLowerCase()}`;
            if (!map.has(k)) map.set(k, e.role);
        }
        return map;
    }, [employees]);

    const itemsWithRole = useMemo(
        () =>
            items.map((i) => {
                const k = `${(i.firstName || "").trim().toLowerCase()}|${(i.lastName || "")
                    .trim()
                    .toLowerCase()}`;
                return { ...i, role: roleByName.get(k) || null };
            }),
        [items, roleByName]
    );

    const grouped = useMemo(() => {
        const g = {
            INSTRUCTOR: [],
            MECHANIC: [],
            GUEST_MANAGER: [],
        };

        for (const r of items) {
            if (r.role && g[r.role]) g[r.role].push(r);
        }

        for (const roleKey of Object.keys(g)) {
            const field = sortField || "lastName";
            const dir = sortDirection === "desc" ? -1 : 1;

            g[roleKey].sort((a, b) => {
                let av, bv;
                switch (field) {
                    case "overtime":
                        av = a.overtime ?? 0; bv = b.overtime ?? 0; break;
                    case "expectedOvertime":
                        av = a.expectedOvertime ?? 0; bv = b.expectedOvertime ?? 0; break;
                    case "lastName":
                    default: {
                        const ln = (a.lastName || "").localeCompare(b.lastName || "", "de", { sensitivity: "base" });
                        if (ln !== 0) return ln * dir;
                        const fn = (a.firstName || "").localeCompare(b.firstName || "", "de", { sensitivity: "base" });
                        return fn * dir;
                    }
                }
                if (av < bv) return -1 * dir;
                if (av > bv) return 1 * dir;
                const ln = (a.lastName || "").localeCompare(b.lastName || "", "de", { sensitivity: "base" });
                if (ln !== 0) return ln;
                return (a.firstName || "").localeCompare(b.firstName || "", "de", { sensitivity: "base" });
            });
        }

        return g;
    }, [items, sortField, sortDirection]);


    // find scale per section (max absolute of both overtime + expected)
    const scales = useMemo(() => {
        const s = {};
        for (const key of Object.keys(grouped)) {
            let maxAbs = 0;
            for (const r of grouped[key]) {
                maxAbs = Math.max(
                    maxAbs,
                    Math.abs(r.overtime || 0),
                    Math.abs(r.expectedOvertime || 0)
                );
            }
            s[key] = maxAbs || 1; // avoid 0/0
        }
        return s;
    }, [grouped]);

    const BarRow = ({ firstName, lastName, overtime = 0, expectedOvertime = 0, scale }) => {
        const ratio = (v) => Math.min(Math.abs(v) / scale, 1) * 50; // -> Prozent der Gesamtbreite
        const baseLeft  = overtime >= 0 ? ratio(overtime) : 0;
        const baseRight = overtime <  0 ? ratio(overtime) : 0;

        const isBad = expectedOvertime > 0;
        const overlayLeft  = isBad ? ratio(expectedOvertime) : 0;
        const overlayRight = !isBad ? ratio(expectedOvertime) : 0;

        const zLeftTopIsOverlay  = overlayLeft && baseLeft ? overlayLeft < baseLeft : !!overlayLeft;
        const zRightTopIsOverlay = overlayRight && baseRight ? overlayRight < baseRight : !!overlayRight;

        const LABEL_MIN = 12; // % Mindestbreite, damit Zahl im Balken bleibt
        const baseVal = numberFmt(overtime);

        const showInsideLeft  = baseLeft  >= LABEL_MIN;
        const showInsideRight = baseRight >= LABEL_MIN;

        const expVal = numberFmt(expectedOvertime);
        const showInsideOverlayLeft  = overlayLeft  >= LABEL_MIN;
        const showInsideOverlayRight = overlayRight >= LABEL_MIN;

        return (
            <div className="py-0">
                <div className="relative h-10 md:h-20 overflow-hidden bg-transparent">
                    {/* GRAU links (Ist, >=0) */}
                    {baseLeft > 0 && (
                        <div
                            className="absolute top-0 h-full bg-gray-300 rounded-l"
                            style={{ width: `${baseLeft}%`, right: "50%", zIndex: zLeftTopIsOverlay ? 10 : 20 }}
                            title={`Überstunden: ${baseVal}`}
                        >
                            {showInsideLeft ? (
                                <div className="h-full flex items-center justify-start pl-2">
                                    <span className="text-gray-700 font-semibold">{baseVal}</span>
                                </div>
                            ) : null}
                        </div>
                    )}
                    {/* Fallback: graue Zahl außerhalb, links neben der Mitte */}
                    {baseLeft > 0 && !showInsideLeft && (
                        <div
                            className="absolute top-0.5 translate-y-0 text-gray-700 font-semibold z-40 pointer-events-none"
                            style={{ right: `calc(50% + ${baseLeft}% + 0.5rem)` }}
                        >
                            {baseVal}
                        </div>
                    )}

                    {/* GRAU rechts (Ist, <0) */}
                    {baseRight > 0 && (
                        <div
                            className="absolute top-0 h-full bg-gray-300 rounded-r"
                            style={{ width: `${baseRight}%`, left: "50%", zIndex: zRightTopIsOverlay ? 10 : 20 }}
                            title={`Überstunden: ${baseVal}`}
                        >
                            {showInsideRight ? (
                                <div className="h-full flex items-center justify-end pr-2">
                                    <span className="text-gray-700 font-semibold">{baseVal}</span>
                                </div>
                            ) : null}
                        </div>
                    )}
                    {/* Fallback: graue Zahl außerhalb, rechts neben der Mitte */}
                    {baseRight > 0 && !showInsideRight && (
                        <div
                            className="absolute top-0.5 translate-y-0 text-gray-700 font-semibold z-40 pointer-events-none"
                            style={{ left: `calc(50% + ${baseRight}% + 0.5rem)` }}
                        >
                            {baseVal}
                        </div>
                    )}


                    {overlayLeft > 0 && (
                        <div
                            className="absolute top-0 h-full bg-emerald-500/80 rounded-l"
                            style={{ width: `${overlayLeft}%`, right: "50%", zIndex: zLeftTopIsOverlay ? 20 : 10 }}
                            title={`Erwartete Überstunden: ${expVal}`}
                        >
                            {showInsideOverlayLeft && (
                                <div className="h-full flex items-center justify-start pl-2">
                                    <span className="text-rose-50 font-semibold">{expVal}</span>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Fallback: rote Zahl außerhalb (links der Mitte) */}
                    {overlayLeft > 0 && !showInsideOverlayLeft && (
                        <div className="absolute bottom-0.5 translate-y-0 text-emerald-600 font-semibold z-40 pointer-events-none"
                             style={{ right: `calc(50% + ${overlayLeft}% + 0.5rem)` }}>
                            {expVal}
                        </div>
                    )}

                    {overlayRight > 0 && (
                        <div
                            className="absolute top-0 h-full bg-rose-500/80 rounded-r"
                            style={{ width: `${overlayRight}%`, left: "50%", zIndex: zRightTopIsOverlay ? 20 : 10 }}
                            title={`Erwartete Überstunden: ${expVal}`}
                        >
                            {showInsideOverlayRight && (
                                <div className="h-full flex items-center justify-end pr-2">
                                    <span className="text-emerald-50 font-semibold">{expVal}</span>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Fallback: grüne Zahl außerhalb (rechts der Mitte) */}
                    {overlayRight > 0 && !showInsideOverlayRight && (
                        <div className="absolute bottom-0.5 translate-y-0 text-rose-600 font-semibold z-40 pointer-events-none"
                             style={{ left: `calc(50% + ${overlayRight}% + 0.5rem)` }}>
                            {expVal}
                        </div>
                    )}


                    {/* NUR Name, über allen Balken */}
                    <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
                        <div className="px-3 py-0.5 rounded-full bg-white/60 backdrop-blur shadow border text-gray-900 font-medium">
                            {(firstName || "").trim()} {(lastName || "").trim()}
                        </div>
                    </div>

                </div>
            </div>
        );
    };

    const Section = ({ roleKey }) => {
        const rows = grouped[roleKey] || [];

        return (
            <section className="mb-12">
                <div className="rounded-2xl bg-white shadow-sm border overflow-hidden">
                    {/* Karten-Header */}
                    <div className={`px-5 py-3 border-b ${ROLE_HEADER_BG[roleKey] || "bg-gray-50 border-gray-100"}`}>
                        <h2 className="text-lg font-semibold text-gray-900">
                            {ROLE_LABELS[roleKey]}
                        </h2>
                    </div>

                    {/* Karten-Body */}
                    <div className="p-4">
                        {rows.length === 0 && !loading && (
                            <div className="py-6 text-center text-gray-500">
                                Keine Einträge in dieser Kategorie.
                            </div>
                        )}

                        <div className="space-y-2">
                            {rows.map((r, i) => (
                                <BarRow
                                    key={`${roleKey}-${i}`}
                                    firstName={r.firstName}
                                    lastName={r.lastName}
                                    overtime={r.overtime}
                                    expectedOvertime={r.expectedOvertime}
                                    scale={scales[roleKey] || 1}
                                />
                            ))}
                        </div>

                        {loading && (
                            <div className="py-6 text-center text-gray-500">Lade Daten…</div>
                        )}
                    </div>
                </div>
            </section>
        );
    };


    return (
        <main className="fixed top-16 inset-0 bg-gray-100 overflow-y-auto">
            <div className="px-16 md:px-16 py-8 max-w-screen-2xl w-full mx-auto">
                <div className="flex items-end gap-3 mb-6 flex-wrap">
                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Sortieren nach</label>
                        <div className="flex items-center gap-2">
                            <select
                                className="border rounded-md px-2 py-2 bg-white text-sm"
                                value={sortField}
                                onChange={(e) => setSortField(e.target.value)}
                            >
                                {SORT_FIELDS.map((f) => (
                                    <option key={f.value} value={f.value}>{f.label}</option>
                                ))}
                            </select>
                            <button
                                className="border rounded-md px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100"
                                onClick={() => setSortDirection((d) => (d === "asc" ? "desc" : "asc"))}
                                title="Richtung umschalten"
                            >
                                {sortDirection === "asc" ? "↑" : "↓"}
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 text-sm text-gray-500 flex flex-wrap gap-3 justify-center">
                        <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-gray-400 inline-block"></span> aktuelle Überstunden
                        </div>
                        <div className="flex items-center gap-2">
                    <span
                        className="w-3 h-3 rounded-full bg-emerald-600 border border-emerald-300 inline-block"></span> erwartete Überstunden
                        </div>
                        <div className="flex items-center gap-2">
                    <span
                        className="w-3 h-3 rounded-full bg-rose-600 border text-rose-600 inline-block"></span> erwartete Überstunden
                        </div>
                    </div>

                    <div className="ml-auto flex items-end gap-3">
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">Endzeitpunkt</label>
                            <input
                                type="datetime-local"
                                className="border rounded-md px-3 py-2 bg-white"
                                value={endTimeLocal}
                                onChange={(e) => setEndTimeLocal(e.target.value)}
                            />
                        </div>

                        <button
                            onClick={fetchAll}
                            className="h-10 px-4 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition"
                            disabled={loading}
                            title="Daten neu laden"
                        >
                            {loading ? "Lade..." : "Aktualisieren"}
                        </button>
                    </div>
                </div>

                {err && (
                    <div className="mb-4 p-3 rounded-md bg-rose-50 border border-rose-200 text-rose-700">
                        {err}
                    </div>
                )}

                {/* three fixed categories */}
                <Section roleKey={ROLE_KEYS.INSTRUCTOR} />
                <Section roleKey={ROLE_KEYS.MECHANIC} />
                <Section roleKey={ROLE_KEYS.GUEST_MANAGER} />
            </div>
        </main>
    );
};

export default Forecast;
